import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { companyInfo } from '../mock';
import { Mail, Send, CheckCircle } from 'lucide-react';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    inquiryType: 'general',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        setSubmitted(true);
        toast.success(response.data.message);
        
        // Reset form after 3 seconds
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            inquiryType: 'general',
            message: ''
          });
          setSubmitted(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Contact form error:', error);
      const errorMessage = error.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="contact"
      style={{
        background: '#FAFAFA',
        padding: '120px 7.6923%',
        position: 'relative'
      }}
    >
      <div 
        style={{
          maxWidth: '900px',
          margin: '0 auto'
        }}
      >
        <div 
          style={{
            textAlign: 'center',
            marginBottom: '60px'
          }}
        >
          <p 
            style={{ 
              color: '#E5A93D', 
              fontSize: '12px', 
              fontWeight: '600', 
              letterSpacing: '2px', 
              marginBottom: '16px',
              textTransform: 'uppercase'
            }}
          >
            get in touch
          </p>
          <h2 
            className="font-display"
            style={{
              fontSize: '48px',
              fontWeight: '700',
              color: '#0D2C22',
              marginBottom: '16px'
            }}
          >
            Ready to start your project?
          </h2>
          <p 
            style={{
              fontSize: '20px',
              color: 'rgba(13, 44, 34, 0.7)',
              lineHeight: '1.6',
              margin: '0 0 8px'
            }}
          >
            Let&apos;s discuss how we can help you scale your business with custom solutions.
          </p>
          <p 
            style={{
              fontSize: '16px',
              color: 'rgba(13, 44, 34, 0.6)',
              lineHeight: '1.6',
              margin: 0
            }}
          >
            You can always send us a general request describing your need and we will discuss solutions suitable for your needs.
          </p>
        </div>
        
        <div 
          style={{
            background: '#0D2C22',
            border: '1px solid rgba(13, 44, 34, 0.1)',
            padding: '60px',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(13, 44, 34, 0.08)'
          }}
        >
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Name */}
              <div>
                <Label 
                  htmlFor="name"
                  style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#0D2C22',
                    marginBottom: '8px',
                    display: 'block'
                  }}
                >
                  Full Name
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Enter your full name"
                  style={{
                    background: '#F7F5F0',
                    border: '1px solid rgba(13, 44, 34, 0.2)',
                    color: '#0D2C22',
                    padding: '14px',
                    fontSize: '16px',
                    borderRadius: '8px',
                    transition: 'all 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#0D2C22';
                    e.target.style.boxShadow = '0 0 0 3px rgba(13, 44, 34, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(13, 44, 34, 0.2)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
              
              {/* Email */}
              <div>
                <Label 
                  htmlFor="email"
                  style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#0D2C22',
                    marginBottom: '8px',
                    display: 'block'
                  }}
                >
                  Email Address
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="your.email@example.com"
                  style={{
                    background: '#F7F5F0',
                    border: '1px solid rgba(13, 44, 34, 0.2)',
                    color: '#0D2C22',
                    padding: '14px',
                    fontSize: '16px',
                    borderRadius: '8px',
                    transition: 'all 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#0D2C22';
                    e.target.style.boxShadow = '0 0 0 2px rgba(13, 44, 34, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(13, 44, 34, 0.2)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
              
              {/* Inquiry Type */}
              <div>
                <Label 
                  htmlFor="inquiryType"
                  style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#0D2C22',
                    marginBottom: '8px',
                    display: 'block'
                  }}
                >
                  Inquiry Type
                </Label>
                <select
                  id="inquiryType"
                  name="inquiryType"
                  value={formData.inquiryType}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    background: '#F7F5F0',
                    border: '1px solid rgba(13, 44, 34, 0.2)',
                    color: '#0D2C22',
                    padding: '14px',
                    fontSize: '16px',
                    borderRadius: '8px',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#0D2C22';
                    e.target.style.boxShadow = '0 0 0 2px rgba(13, 44, 34, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(13, 44, 34, 0.2)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="ai-landing-page">AI Landing Page</option>
                  <option value="ai-website">AI Website Building (Fullstack App)</option>
                  <option value="business-process">Business Process Analysis</option>
                  <option value="business-development">Business Development Case Request</option>
                  <option value="customized-saas">Customized SaaS Solutions</option>
                  <option value="general">General Request / Quote</option>
                  <option value="saas-sales-leads-pro">SaaS Sales Leads Pro Quote</option>
                </select>
              </div>
              
              {/* Message */}
              <div>
                <Label 
                  htmlFor="message"
                  style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#0D2C22',
                    marginBottom: '8px',
                    display: 'block'
                  }}
                >
                  Message
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  placeholder="Tell us about your needs..."
                  rows={6}
                  style={{
                    background: '#F7F5F0',
                    border: '1px solid rgba(13, 44, 34, 0.2)',
                    color: '#0D2C22',
                    padding: '14px',
                    fontSize: '16px',
                    borderRadius: '8px',
                    transition: 'all 0.3s ease',
                    resize: 'vertical'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#0D2C22';
                    e.target.style.boxShadow = '0 0 0 2px rgba(13, 44, 34, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(13, 44, 34, 0.2)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
              
              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting || submitted}
                className="btn-primary"
                style={{
                  width: '100%',
                  justifyContent: 'center',
                  opacity: (isSubmitting || submitted) ? 0.7 : 1,
                  cursor: (isSubmitting || submitted) ? 'not-allowed' : 'pointer'
                }}
              >
                {submitted ? (
                  <>
                    <CheckCircle size={20} />
                    Message Sent
                  </>
                ) : isSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    <Send size={20} />
                    Send Message
                  </>
                )}
              </button>
            </div>
          </form>
          
          {/* Message sent confirmation - no email display */}
          <div style={{ display: 'none' }}>
            {/* Email info removed for privacy */}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
